// src/sot/primitives/AfriMedDialog.jsx
import React from "react";
import { createPortal } from "react-dom";

/**
 * AfriMedDialog
 *
 * SSOT for centred dialogs:
 * - Backdrop, max-width, radius, premium shadow
 * - Fade + scale-in animation
 * - Optional header (title + subtitle)
 * - Optional generated footer (primary / secondary buttons)
 *
 * You can either:
 *  - Pass your own `children` and `footer`, OR
 *  - Use `primaryLabel`, `secondaryLabel` + handlers for a standard footer
 */
export default function AfriMedDialog({
  open,
  onClose,
  title,
  subtitle,
  children,
  footer = null,
  primaryLabel,
  onPrimary,
  secondaryLabel,
  onSecondary,
  alignButtons = "center", // "center" | "end"
  headerAction = null,
  returnFocusRef = null,
  dialogLabel,
}) {
  const dialogRef = React.useRef(null);
  const priorFocusRef = React.useRef(null);

  const requestClose = React.useCallback(() => {
    onClose?.();
  }, [onClose]);

  React.useEffect(() => {
    if (!open || typeof document === "undefined") return undefined;

    priorFocusRef.current = document.activeElement;
    const priorOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusDialog = () => {
      const focusable = dialogRef.current?.querySelector(
        'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
      );
      (focusable || dialogRef.current)?.focus?.();
    };

    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        requestClose();
        return;
      }
      if (event.key !== "Tab") return;
      const focusable = Array.from(
        dialogRef.current?.querySelectorAll(
          'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
        ) || [],
      );
      if (!focusable.length) {
        event.preventDefault();
        dialogRef.current?.focus?.();
        return;
      }
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", handleKeyDown);
    const frame = window.requestAnimationFrame(focusDialog);
    return () => {
      window.cancelAnimationFrame(frame);
      document.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = priorOverflow;
      window.requestAnimationFrame(() => {
        (returnFocusRef?.current || priorFocusRef.current)?.focus?.();
      });
    };
  }, [open, requestClose, returnFocusRef]);

  if (!open || typeof document === "undefined") return null;

  const handleSecondary = () => {
    if (onSecondary) onSecondary();
    else if (onClose) onClose();
  };

  const autoFooter =
    footer ||
    ((primaryLabel || secondaryLabel) && (
      <div
        className={`px-5 py-4 flex gap-3 ${
          alignButtons === "end" ? "justify-end" : "justify-center"
        }`}
      >
        {secondaryLabel && (
          <button
            type="button"
            onClick={handleSecondary}
            className="px-5 py-2.5 rounded-xl text-label-large border"
            style={{
              borderColor: "rgba(24,50,119,0.18)",
              background: "transparent",
              color: "var(--on-surface)",
            }}
          >
            {secondaryLabel}
          </button>
        )}

        {primaryLabel && (
          <button
            type="button"
            onClick={onPrimary}
            className="px-5 py-2.5 rounded-xl text-label-large"
            style={{
              background: "var(--state-primary-12)",
              color: "var(--on-primary)",
            }}
          >
            {primaryLabel}
          </button>
        )}
      </div>
    ));

  return createPortal(
    <div
      className="fixed inset-0 z-[1000] grid place-items-center overflow-y-auto bg-black/40 px-4 py-4 sm:px-6"
      onMouseDown={requestClose}
    >
      <div
        ref={dialogRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-label={dialogLabel || (typeof title === "string" ? title : "Dialog")}
        className="
          w-full max-w-[560px] min-w-0
          max-h-[calc(100dvh-32px)]
          rounded-[28px]
          bg-[rgb(var(--clr-surface-cont-highest))]
          shadow-[0_1px_2px_rgba(16,24,40,0.06),0_8px_24px_rgba(16,24,40,0.06)]
          overflow-hidden outline-none
          animate-afrimed-dialog-in
        "
        onMouseDown={(event) => event.stopPropagation()}
      >
        {(title || subtitle || headerAction) && (
          <div className="flex items-start justify-between gap-4 px-6 pb-4 pt-6 sm:px-7">
            <div className="min-w-0">
              {title && <h2 className="text-headline-small text-on-surface">{title}</h2>}
              {subtitle && <p className="mt-1 text-body-medium text-on-surface-var">{subtitle}</p>}
            </div>
            {headerAction}
          </div>
        )}

        {children}

        {autoFooter}
      </div>

      <style>{`
        @keyframes afrimed-dialog-in {
          from {
            opacity: 0;
            transform: translateY(8px) scale(0.98);
          }
          to {
            opacity: 1;
            transform: translateY(0) scale(1);
          }
        }
        .animate-afrimed-dialog-in {
          animation: afrimed-dialog-in 180ms ease-out;
        }
      `}</style>
    </div>,
    document.body,
  );
}
